<template>
  <div>
    <form @submit.prevent="handleOk">
      <b-modal
        id="showModal"
        v-model="showModal"
        size="md"
        no-close-on-backdrop
        no-close-on-esc
        @shown="reset"
      >
        <div slot="modal-header" class="w-100">
          <font-awesome-icon icon="bars" />
          <span class="pl-2">
            {{ input ? 'Edit' : 'Add' }} Review Setting
          </span>
        </div>
        <div>
          <b-container>
            <b-row class="mt-2">
              <b-col md="3">
                <label>Title</label>
              </b-col>
              <b-col md="9" :class="{'form-error': errors.first('title')}">
                <b-form-input
                  v-model="obj.title"
                  v-validate="'required'"
                  name="title"
                  type="text"
                  :placeholder="`Enter title, use {\{username\}} to insert username`"
                />
                <small>{{ errors.first('title') }}</small>
              </b-col>
            </b-row>
            <b-row class="mt-2">
              <b-col md="3">
                <label>Outlet Code</label>
              </b-col>
              <b-col md="9" :class="{'form-error': errors.first('outletCode')}">
                <b-form-input
                  v-if="isAdmin"
                  v-model="obj.outletCode" v-validate="'required'" name="outletCode" type="number" placeholder="Enter Outlet Code"
                />
                <b-form-select
                  v-if="!isAdmin"
                  v-model="obj.outletCode" v-validate="'required'" :options="outletCodes" name="outletCode" type="number" placeholder="Enter Outlet Code"
                />
                <small>{{ errors.first('outletCode') }}</small>
              </b-col>
            </b-row>
            <b-row class="mt-2">
              <b-col md="3">
                <label>Form Threshold</label>
              </b-col>
              <b-col md="9" :class="{'form-error': errors.first('formThreshold')}">
                <b-form-input
                  v-model.number="obj.formThreshold"
                  v-validate="'required'" name="formThreshold" type="number" :min="1" :max="5" placeholder="Enter Form Threshold"
                />
                <small>{{ errors.first('formThreshold') }}</small>
              </b-col>
            </b-row>
            <b-row class="mt-2">
              <b-col md="3">
                <label>Question</label>
              </b-col>
              <b-col md="9" :class="{'form-error': errors.first('question')}">
                <b-form-input
                  v-model="obj.question"
                  v-validate="'required'" name="question" type="text" placeholder="Enter Question"
                />
                <small>{{ errors.first('question') }}</small>
              </b-col>
            </b-row>
            <b-row class="mt-2">
              <b-col md="3">
                <label>Google Review Link</label>
              </b-col>
              <b-col md="9" :class="{'form-error': errors.first('googleReviewLink')}">
                <b-form-input
                  v-model="obj.googleReviewLink"
                  v-validate="'required'" name="googleReviewLink" type="text" placeholder="Enter Google Review Link"
                />
                <small>{{ errors.first('googleReviewLink') }}</small>
              </b-col>
            </b-row>
            <b-row class="mt-2">
              <b-col md="3">
                <label>Background Color (Hex Code)</label>
              </b-col>
              <b-col md="9" :class="{'form-error': errors.first('backgroundColor')}">
                <b-form-input
                  v-model="obj.backgroundColor"
                  name="backgroundColor" type="text" placeholder="Enter Background Color"
                />
                <small>{{ errors.first('backgroundColor') }}</small>
              </b-col>
            </b-row>
            <b-row class="mt-2">
              <b-col md="3">
                <label>Text Color (Hex Code)</label>
              </b-col>
              <b-col md="9" :class="{'form-error': errors.first('textColor')}">
                <b-form-input
                  v-model="obj.textColor"
                  name="textColor" type="text" placeholder="Enter Text Color"
                />
                <small>{{ errors.first('textColor') }}</small>
              </b-col>
            </b-row>
            <b-row class="mt-2">
              <b-col md="3">
                <label>Logo</label>
              </b-col>
              <b-col md="9">
                <b-row>
                  <b-col cols="9">
                    <b-form-file
                      v-model="logo"
                      type="file"
                      placeholder="Upload logo..."
                      accept="image/png, image/jpg, image/jpeg, image/gif, image/webp"
                      name="file-upload"
                    />
                  </b-col>
                  <b-col cols="3">
                    <b-button
                      :disabled="uploading || !logo"
                      variant="success"
                      @click="handleUpload"
                    >
                      <b-spinner v-if="uploading" small /> Upload
                    </b-button>
                  </b-col>
                </b-row>
                <div style="margin-top: 10px"></div>
                <a v-if="obj.logo" :href="obj.logo" target="_blank">Uploaded Logo</a>
                <span v-if="obj.logo" style="cursor: pointer; margin-left: 5px;" @click="discardLogo">X</span>
                <small>{{ errors.first('logo') }}</small>
              </b-col>
            </b-row>
          </b-container>
        </div>
        <div slot="modal-footer" class="w-100">
          <b-btn type="submit" variant="success" :disabled="saveLoading || uploading">
            <b-spinner v-if="saveLoading" small />
            <font-awesome-icon v-if="!saveLoading" icon="save" />
            {{ input ? 'Update' : 'Save' }} Data
          </b-btn>
          <b-btn class="float-right" variant="danger" @click="discardModal">
            <font-awesome-icon icon="times" />Discard
          </b-btn>
        </div>
      </b-modal>
    </form>
  </div>
</template>

<script>
import _ from 'lodash'
import {
  getFileName,
  getFileNameWithUUID,
  getResponseToMTH
} from '@/utils/helpers'
import constants from '@/utils/constants'
import { firebaseApp, firebaseStorage } from '@/firebase'

export default {
  props: {
    input: {
      type: null,
      default: null
    },
    showModal: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      obj: {},
      saveLoading: false,
      isAdmin: this.$store.state.currentUser.Type === 'Admin',
      outletCodes: [
        { text: 'Please Select', value: '' },
        ...this.$store.state.currentUser.OutletCodes.map(item => ({
          text: item,
          value: item
        }))
      ],
      logo: null,
      uploading: false
    }
  },

  computed: {
    getOutletPriceConfig() {
      return _.values(this.$store.getters.GetOutletPriceConfig.ORDER_TYPES)
    }
  },

  created() {
    this.$store.dispatch('GetOutletPriceConfig')
  },

  methods: {
    // add and update records
    handleOk(evt) {
      this.$validator.validate().then(valid => {
        if (valid) {
          this.saveLoading = true
          evt.preventDefault()
          if (this.input === null) {
            this.$store
              .dispatch('CreateReview', this.obj)
              .then(response => {
                let res = getResponseToMTH(
                  response,
                  this.$swal,
                  'Record is created'
                )
                if (res) {
                  this.reset()
                  this.$emit('discard-modal', true)
                }
                this.saveLoading = false
              })
              .catch(error => {
                this.$swal(
                  constants.SOMETHING_WROING,
                  error.response.data.message,
                  constants.STATUS_ERROR
                )
                this.saveLoading = false
              })
          } else {
            this.$store
              .dispatch('UpdateReview', this.obj)
              .then(response => {
                let res = getResponseToMTH(
                  response,
                  this.$swal,
                  'Record is updated'
                )
                if (res) {
                  this.reset()
                  this.$emit('discard-modal', true)
                }
                this.saveLoading = false
              })
              .catch(error => {
                this.$swal(
                  constants.SOMETHING_WROING,
                  error.response.data.message,
                  constants.STATUS_ERROR
                )
                this.saveLoading = false
              })
          }
        }
      })
    },
    discardModal() {
      this.reset()
      this.$emit('discard-modal')
    },
    handleUpload() {
      this.uploading = true
      try {
        const fileName = getFileNameWithUUID(this.logo.name)
        const uploadTask = firebaseStorage
          .ref()
          .child(`OutletContract/${fileName}`)
          .put(this.logo)
        uploadTask.on(
          firebaseApp.storage.TaskEvent.STATE_CHANGED,
          () => {},
          () => {},
          () => {
            uploadTask.snapshot.ref.getDownloadURL().then(downloadURL => {
              this.logo = null
              this.obj.logo = downloadURL
            })
            this.uploading = false
          }
        )
      } catch (e) {
        this.uploading = false
        console.error(e)
      }
    },
    discardLogo() {
      this.logo = null
      this.obj.logo = ''
    },

    // reset input fields
    reset() {
      if (this.input == null) {
        this.obj = {
          title: '',
          outletCode: '',
          formThreshold: '',
          question: '',
          googleReviewLink: '',
          backgroundColor: 'rgb(221, 232, 236)',
          textColor: '#333',
          logo: ''
        }
      } else {
        this.obj = {
          formThreshold: this.input.formThreshold,
          googleReviewLink: this.input.googleReviewLink,
          id: this.input.id,
          outletCode: this.input.outletCode,
          question: this.input.question,
          status: this.input.status,
          title: this.input.title,
          backgroundColor: this.input.backgroundColor || 'rgb(221, 232, 236)',
          textColor: this.input.textColor || '#333',
          logo: this.input.logo
        }
      }
    }
  }
}
</script>
